import { AlertTriangle, TrendingDown, CheckCircle2 } from "lucide-react";

// Resumen de tesorería en vivo del presupuesto (escenario activo). Es 100%
// derivado de `computed` (que ya se recalcula al editar), así que se actualiza
// solo conforme metes/cambias partidas. Traduce las cifras a la pregunta que
// importa: ¿me quedo sin caja? ¿cuánto necesito ingresar para no hacerlo?
const eur0 = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n ?? 0);

const MONTH_NAMES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function monthLabel(mo) {
  if (!mo) return "";
  const m = parseInt(mo.slice(5, 7), 10);
  return `${MONTH_NAMES[m - 1] ?? mo} ${mo.slice(2, 4)}`;
}

export default function BudgetCashSummary({ scData, months, openingBalance, scenarioLabel }) {
  const totals = scData?.totals;
  const minCash = scData?.min_cash;
  if (!totals || !minCash || !months?.length) return null;

  const n = months.length;
  const totalIn = totals.total_income ?? 0;
  const totalOut = totals.total_expense ?? 0;
  const cashFinal = totals.cash_final ?? 0;
  const lowest = minCash.value ?? 0;
  // Colchón prudente de referencia: 1 mes de gasto medio del periodo.
  const cushion = n ? totalOut / n : 0;
  const needSurvive = lowest < 0 ? -lowest : 0;          // para no entrar en negativo
  const needCushion = lowest < cushion ? cushion - lowest : 0;  // para mantener el colchón
  const perMonth = (x) => (n ? x / n : 0);

  let tone, Icon, title, body;
  if (lowest < 0) {
    tone = "red";
    Icon = AlertTriangle;
    title = "Te quedas sin caja";
    body = `En ${monthLabel(minCash.month)} tu caja baja a ${eur0(lowest)}. Necesitas ingresar al menos `
      + `${eur0(needSurvive)} más en el periodo (≈ ${eur0(perMonth(needSurvive))}/mes) solo para no entrar en `
      + `negativo, y unos ${eur0(needCushion)} (≈ ${eur0(perMonth(needCushion))}/mes) para mantener un colchón `
      + `de ${eur0(cushion)} (1 mes de gastos).`;
  } else if (lowest < cushion) {
    tone = "amber";
    Icon = TrendingDown;
    title = "Sobrevives, pero con poco margen";
    body = `Tu caja baja hasta ${eur0(lowest)} en ${monthLabel(minCash.month)}, por debajo de un colchón prudente `
      + `de ${eur0(cushion)} (1 mes de gastos). Para mantenerlo, te faltaría ingresar ≈ ${eur0(needCushion)} en el `
      + `periodo (≈ ${eur0(perMonth(needCushion))}/mes).`;
  } else {
    tone = "green";
    Icon = CheckCircle2;
    title = "Vas holgado";
    body = `Tu caja nunca baja de ${eur0(lowest)} (en ${monthLabel(minCash.month)}) y terminas el periodo con `
      + `${eur0(cashFinal)}.`;
  }

  const toneCls = {
    red: "border-red-200 bg-red-50 text-red-800",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    green: "border-emerald-200 bg-emerald-50 text-emerald-900",
  }[tone];

  return (
    <div className={`mb-4 rounded-lg border p-4 ${toneCls}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">
            {title} <span className="font-normal opacity-70">· escenario {scenarioLabel}</span>
          </div>
          <p className="text-sm mt-0.5">{body}</p>
          <p className="text-[11px] opacity-60 mt-1">
            El «/mes» es orientativo (reparte el total entre los {n} meses del periodo).
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3 text-center">
        <Cell label="Caja inicial" value={eur0(openingBalance)} />
        <Cell label="Entra (total)" value={eur0(totalIn)} />
        <Cell label="Sale (total)" value={eur0(totalOut)} />
        <Cell label="Punto más bajo" value={eur0(lowest)} sub={monthLabel(minCash.month)} />
        <Cell label="Caja final" value={eur0(cashFinal)} />
      </div>
    </div>
  );
}

function Cell({ label, value, sub }) {
  return (
    <div className="rounded-md bg-white/60 px-2 py-1.5">
      <div className="text-[11px] opacity-70">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
      {sub ? <div className="text-[11px] opacity-60">{sub}</div> : null}
    </div>
  );
}
